/**
 * The admin console's status controls (spec §7.1). A map rather than the
 * ternary this replaced: with four statuses a two-state toggle rendered a
 * "Publish" button on an archived tournament. The transitions here mirror the
 * server-side guard in repositories.ts, so the UI cannot offer a 409.
 */
import type { TournamentStatus } from "@alpha/shared";
import type { Bundle } from "./types";

export interface TournamentAction {
  label: string;
  next: TournamentStatus;
  variant: "primary" | "secondary";
  /** Ask for confirmation first — this one pulls a public event offline. */
  confirm?: boolean;
}

export function statusActions(status: TournamentStatus): TournamentAction[] {
  switch (status) {
    case "draft":
      return [{ label: "Publish", next: "published", variant: "primary" }];
    case "published":
      return [{ label: "Mark complete", next: "complete", variant: "secondary" }];
    case "complete":
      return [
        { label: "Publish", next: "published", variant: "secondary" },
        { label: "Archive", next: "archived", variant: "secondary", confirm: true },
      ];
    case "archived":
      return [{ label: "Unarchive", next: "complete", variant: "primary" }];
  }
}

/**
 * What an organizer is about to go live, or close out, without (console spec
 * §3.4). Warnings, not blocks: the API accepts either transition regardless,
 * and a bracket-only event or a pool-play-only event has a reason to go ahead.
 * The bracket lines fire only for a division that started toward one, so a
 * pool-play-only organizer is not nagged.
 *
 * Pure, so the list can be tested — the strip that shows it opens on a click,
 * which a static render never sees.
 */
export function readinessWarnings(next: TournamentStatus, bundle: Bundle): string[] {
  const { tournament: t, divisions, pools, games } = bundle;
  const warnings: string[] = [];

  if (next === "published") {
    if (divisions.length === 0) warnings.push("No divisions yet");
    for (const d of divisions) {
      if (!pools.some((p) => p.divisionId === d.id)) warnings.push(`${d.name} has no pools`);
    }
    if (!t.location) warnings.push("No city set");
    if (!t.registration) warnings.push("No registration window — teams can't register");
  }

  if (next === "complete") {
    const open = games.filter((g) => g.status === "scheduled").length;
    if (open > 0) warnings.push(`${open} ${open === 1 ? "game has" : "games have"} no result`);
    for (const d of divisions) {
      const bracket = games.filter((g) => g.divisionId === d.id && g.type === "bracket");
      if (d.lockedSeeds && bracket.length === 0) {
        warnings.push(`${d.name} has locked seeds but no bracket`);
      }
      if (bracket.length > 0 && bracket.some((g) => g.status === "scheduled")) {
        warnings.push(`${d.name}'s bracket is unfinished`);
      }
    }
  }

  return warnings;
}
